import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../../../contexts/AuthContext";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { CodeInputWithResend } from "../CodeInputWithResend";
import LoadingSpinner from "../../loading-spinner/LoadingSpinner";

export default function ForgotPassForm() {
  const FORGOT_STEP_KEY = "mb_forgot_step";

  const navigate = useNavigate();
  const [email, setEmail] = useState(() => localStorage.getItem("mb_userEmail") || "");
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [step, setStep] = useState(1);
  const { forgotPassword, confirmPasswordReset, loading } = useAuth();

  useEffect(() => {
    // If a reset is in progress (we've requested a code), keep the user on step 2 after refresh.
    const savedEmail = localStorage.getItem("mb_userEmail") || "";
    const savedStep = localStorage.getItem(FORGOT_STEP_KEY);

    if (savedEmail && savedStep === "2") {
      setEmail(savedEmail);
      setStep(2);
    }
  }, []);

  const handleRequestCode = async (e) => {
    e.preventDefault();
    try {
      await forgotPassword(email);
      alert("Password reset code sent to your email!");
      localStorage.setItem("mb_userEmail", email);
      localStorage.setItem(FORGOT_STEP_KEY, "2");
      setStep(2);
    } catch (err) {
      if (err.message === "Username/client id combination not found.")
      {
        alert("No account found with this email.");
        navigate("/signup");
      }
      else{
      alert("Error requesting password reset: " + err.message);
      }
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      alert("Passwords do not match!");
      return;
    }

    try {
      await confirmPasswordReset(email, code, newPassword);
      alert("Password reset successfully! You can now login.");
      localStorage.removeItem(FORGOT_STEP_KEY);
      navigate("/login");
    } catch (err) {
      alert("Error resetting password: " + err.message);
    }
  };

  const handleResendCode = async () => {
    await forgotPassword(email);
    alert("Password reset code resent!");
  };

  const handleChangeEmail = () => {
    // Clear the countdown for the old email
    const oldEmail = email;
    if (oldEmail) {
      const storageKey = `codeResendCountdownEnd_${oldEmail}`;
      localStorage.removeItem(storageKey);
    }
    
    // Clear step 2 state
    localStorage.removeItem(FORGOT_STEP_KEY);
    setCode("");
    setNewPassword("");
    setConfirmPassword("");
    
    // Go back to step 1
    setStep(1);
  };

   if (loading) {
      return (
        <div className="p-4 flex items-center justify-center min-h-[40vh]">
          <LoadingSpinner />
        </div>
      );
    }

  return (
    <div className="h-[calc(100vh-40px)] flex items-center justify-center px-4">
      <div className="mx-auto w-full max-w-sm space-y-6">
        {step === 1 ? (
          <>
            <div className="text-center space-y-2">
              <h1 className="text-2xl font-semibold tracking-tight">
                Forgot Password
              </h1>
              <p className="text-sm text-muted-foreground">
                Enter your email to receive a password reset code
              </p>
            </div>

            <form onSubmit={handleRequestCode} className="space-y-4">
              <div className="space-y-1">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>

              <Button type="submit" className="w-full" disabled={loading}>
                Send Reset Code
              </Button>
            </form>
          </>
        ) : (
          <>
            <div className="text-center space-y-2">
              <h1 className="text-2xl font-semibold tracking-tight">
                Reset Password
              </h1>
              <p className="text-sm text-muted-foreground">
                Enter the code sent to {email}
              </p>
              <button
                type="button"
                onClick={handleChangeEmail}
                className="text-sm text-blue-600 hover:text-blue-800 underline underline-offset-4"
              >
                Use a different email
              </button>
            </div>

            <form onSubmit={handleResetPassword} className="space-y-4">
              <CodeInputWithResend
                code={code}
                setCode={setCode}
                onResend={handleResendCode}
                email={email}
                label="Reset Code"
              />

              <div className="space-y-1">
                <Label htmlFor="newPassword">New Password</Label>
                <Input
                  id="newPassword"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                />
              </div>

              <div className="space-y-1">
                <Label htmlFor="confirmPassword">Confirm Password</Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                />
              </div>

              <Button type="submit" className="w-full" disabled={loading}>
                Reset Password
              </Button>
            </form>
          </>
        )}
        
        <p className="px-8 text-center text-sm text-muted-foreground">
          <Link to="/login" className="underline underline-offset-4">
            Back to login
          </Link>
        </p>
      </div>
    </div>
  );
}